import type { PolicyModeType } from '@kirin/api-client/contracts/policies';
import { PolicyMode } from '@kirin/api-client/contracts/policies';
import { AI_INSTRUCTION_POLICY_GROUP } from '@kirin/api-client/contracts/ai-instruction-catalog';

/**
 * Constants for policy settings
 * Centralized location for magic strings and special case identifiers
 *
 * Note: These are used as fallbacks when schema-based derivation fails.
 * Most field rendering is now derived from validation.pattern and validation.properties.
 */

/** Display labels for Off / Monitor / Enforce policy modes */
export const MODE_LABELS: Record<PolicyModeType, string> = {
  [PolicyMode.Off]: 'Off',
  [PolicyMode.Monitor]: 'Monitor',
  [PolicyMode.Enforce]: 'Enforce',
};

/**
 * Special setting keys that require custom rendering or validation
 * Used as fallback when schema derivation doesn't work
 */
export const SPECIAL_SETTINGS = {
  AI_CODE_SENSITIVITY: 'aiCodeSensitivity',
} as const;

/**
 * Policy keys that require special handling
 * Used as fallback when schema derivation doesn't work
 */
export const POLICY_KEYS = {
  EXTENSION_SECURITY: 'extension-security',
} as const;

/**
 * Allow/Block policies (KIR-1038 MCPs, KIR-1647 Extensions): no policy-level
 * Off/Monitor/Enforce on the collapsed row — tri-state belongs on the **Blocked** list inside settings (TODO there).
 */
export const POLICY_KEYS_HIDE_OUTER_MODE_CONTROLS: readonly string[] = [
  'extensions-management',
  'mcp-server-sources',
] as const;

export function policyKeyHidesOuterModeControls(policyKey: string): boolean {
  return (POLICY_KEYS_HIDE_OUTER_MODE_CONTROLS as readonly string[]).includes(
    policyKey
  );
}

/**
 * Backend `PolicyCategory.SECURE_AI_CODING` value (KIR-3816). The Secure AI
 * Coding Enforcement policy has its own top-level page, so `PoliciesList`
 * excludes this category from the Policies list. Named here to avoid a bare
 * string literal in the filter; drift from the backend value is guarded by the
 * exclusion test in `PoliciesList.test.tsx`.
 */
export const SECURE_AI_CODING_POLICY_CATEGORY = 'secure-ai-coding-policies';

/**
 * Backend `PolicyCategory.THREAT_MODELING` value (KIR-3925). Same deal as
 * Secure AI Coding: the Threat Modeling policy has its own AI AppSec page, so
 * `PoliciesList` excludes this category from the Policies list.
 */
export const THREAT_MODELING_POLICY_CATEGORY = 'threat-modeling-policies';

// KIR-3982: each category below owns a Policies sub-page; other categories fall
// to the default Detection page. `PoliciesList` filters by PolicySection.
export const ALLOW_BLOCK_POLICY_CATEGORY = 'configuration-policies';
export const AI_INSTRUCTION_POLICY_CATEGORY = 'ai-instruction-policies';

/** Categories that own a dedicated sub-page; everything else lands on Detection. */
export const NON_DETECTION_POLICY_CATEGORIES: readonly string[] = [
  AI_INSTRUCTION_POLICY_CATEGORY,
  ALLOW_BLOCK_POLICY_CATEGORY,
];

/** Which Policies sub-page `PoliciesList` renders. */
export type PolicySection = 'detection' | 'ai-instructions' | 'allow-block';

export type PolicyCategoryMetadata = Record<
  string,
  { name: string; description: string; order: number }
>;
export const POLICY_CATEGORIES_META: PolicyCategoryMetadata = {
  'attack-detection-policies': {
    name: 'Detection Policies',
    description:
      'Detects active attacks, malicious content, and security threats. These policies are enabled by default to provide immediate protection.',
    order: 1,
  },
  // KIR-3515: shown below Detection Policies, above Allow/Block Lists.
  // Name + description are single-sourced from the api-client contract so they
  // can't drift from the backend policy group.
  'ai-instruction-policies': {
    name: AI_INSTRUCTION_POLICY_GROUP.name,
    description: AI_INSTRUCTION_POLICY_GROUP.description,
    order: 2,
  },
  'configuration-policies': {
    name: 'Allow/Block Lists',
    description: 'Manage Allow and Block lists.',
    order: 3,
  },
};
